import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { ExternalLink, Eye } from "lucide-react";
import { Button } from "@headerhawk/ui";
import { readImpersonationState } from "@/lib/auth/impersonation";
import { DemoEvents, trackDemoEvent } from "@/lib/analytics";
import { DEMO_SHOP_URL } from "@/lib/demo/start";
import { sectionForPath } from "@/lib/navigation";
import { DEMO_STARTED_FLAG, signUpFromDemo } from "@/lib/demo/session";
import { useBannerOffset } from "@/lib/hooks/use-banner-offset";

/** How often the minutes left are redrawn. */
const TICK_MS = 15_000;

/**
 * The public demo's banner (task 405): above every screen of a running demo,
 * and not dismissable. One line: what the visitor is looking at and how long
 * it lasts, the demo shop, whose visits reach this dashboard within a minute,
 * and signup. Which plan a feature needs is a `PlanBadge` on the feature
 * itself (task 450), not a second line here.
 *
 * It also sends the demo's own events, since it is the one component inside
 * the router for the whole of a demo: `demo_started` once, on the first page
 * after the mint, and `demo_section` for each section viewed.
 */
export function DemoBanner() {
  const { pathname } = useLocation();
  const sectionKey = sectionForPath(pathname)?.key;
  const [leaving, setLeaving] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const expiresAt = readImpersonationState()?.expiresAt;
  const expiry = expiresAt ? Date.parse(expiresAt) : NaN;
  const bannerRef = useRef<HTMLDivElement>(null);
  useBannerOffset(bannerRef);

  useEffect(() => {
    let started = false;
    try {
      started = window.sessionStorage.getItem(DEMO_STARTED_FLAG) !== null;
      window.sessionStorage.removeItem(DEMO_STARTED_FLAG);
    } catch {
      // No storage, no event.
    }
    if (started) trackDemoEvent(DemoEvents.STARTED);
  }, []);

  const lastSection = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!sectionKey || sectionKey === lastSection.current) return;
    lastSection.current = sectionKey;
    trackDemoEvent(DemoEvents.SECTION, { section: sectionKey });
  }, [sectionKey]);

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(tick);
  }, []);

  const minutesLeft = Number.isFinite(expiry)
    ? Math.max(1, Math.ceil((expiry - now) / 60_000))
    : null;

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label="Demo organization"
      data-demo-banner=""
      className="sticky top-0 z-50 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-sky-500/40 bg-sky-100 px-4 py-2 text-sm text-sky-950 dark:bg-sky-950 dark:text-sky-50"
    >
      <Eye className="h-4 w-4 shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1">
        <strong>Demo organization: read-only.</strong> Nothing you do here is
        saved.
        {minutesLeft !== null && (
          <span data-demo-minutes-left="">
            {" "}
            Ends in {minutesLeft} {minutesLeft === 1 ? "minute" : "minutes"}.
          </span>
        )}
      </p>
      {DEMO_SHOP_URL && (
        <Button asChild size="sm" variant="outline">
          <a
            href={DEMO_SHOP_URL}
            target="_blank"
            rel="noopener"
            onClick={() => trackDemoEvent(DemoEvents.SHOP_OPEN)}
          >
            Open the demo shop
            <ExternalLink className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
          </a>
        </Button>
      )}
      <Button
        type="button"
        size="sm"
        disabled={leaving}
        onClick={() => {
          setLeaving(true);
          void signUpFromDemo("demo_banner");
        }}
      >
        Sign up free
      </Button>
    </div>
  );
}
